import { useEffect, useMemo, useState } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { Route } from '~/Constants/Routes.constants';
import { isUserConfirmingEmail, isUserVerifyingEmail } from '~/Helpers/Route.helpers';
import { useHasAccess, useNavigation, useOrganisation, useProvider, useUser } from '~/Hooks';
import { useLocalStorage } from '~/Hooks/useLocalStorage';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { EmailVerificationEnum } from '~/Models/DTO/User.model';

const { HOME, LOGIN, LOGIN_USC, ORGANISATION_DETAIL, ORGANISATION_ROOT } = Route;

export const useOrganisationRoutes = () => {
  const loc = useLocation();
  const { accessToken } = useLocalStorage();
  const { hasAccess } = useHasAccess();
  const { navigate } = useNavigation();
  const { currentUser, hasUserLoaded, userError, hasRolesLoaded } = useUser();
  const { currentProvider, isUsingThePartnerTool } = useProvider();
  const { organisation, organisationError, isOrganisationFetched } = useOrganisation();
  const isDomainMissing = !useRouteMatch<{ domain?: string }>({
    path: ORGANISATION_DETAIL,
    location: loc,
  });
  const isOnOrganisationRoute = loc.pathname.startsWith(ORGANISATION_ROOT);

  const [logOutURL, setLogOutURL] = useState(LOGIN);
  const [localOrganisationError, setLocalOrganisationError] = useState(
    undefined as IErrorResponse | undefined,
  );
  const isLoggedIn = useMemo(() => !!accessToken, [accessToken]);
  const isOnLandingPage = useMemo(() => loc.pathname === ORGANISATION_ROOT, [loc.pathname]);
  const isAccessingAPrivatePage = useMemo(
    () => loc && !hasAccess(loc.pathname) && loc.pathname !== HOME,
    [loc.pathname, hasAccess],
  );
  const isUserNotVerified = useMemo(
    () => !currentUser || currentUser?.isVerified === EmailVerificationEnum.NOT_VERIFIED,
    [currentUser],
  );
  const shouldGoToTheDashboard = useMemo(
    () => organisation && (isOnLandingPage || isAccessingAPrivatePage || isDomainMissing),
    [organisation?.id, isOnLandingPage, isAccessingAPrivatePage, isDomainMissing],
  );

  const isNoOrganisationSelected = useMemo(
    () =>
      currentUser?.isVerified !== EmailVerificationEnum.NOT_VERIFIED &&
      !organisation &&
      // I'm keeping these as a reminder that we also need an organisation selection page
      // hasAvailableProvidersLoaded &&
      // availableProviders?.length > 1 &&
      !isUserVerifyingEmail() &&
      !isUserConfirmingEmail() &&
      !isOnOrganisationRoute &&
      !isOrganisationFetched,
    [
      currentUser,
      organisation,
      // hasAvailableProvidersLoaded,
      // availableProviders,
      loc.pathname,
      isUserVerifyingEmail,
      isUserConfirmingEmail,
      isOrganisationFetched,
    ],
  );

  useEffect(() => {
    if (currentProvider) {
      if (!currentProvider.organisationId) {
        setLocalOrganisationError({ message: 'error' });
      }
      setLogOutURL(isUsingThePartnerTool ? LOGIN_USC : LOGIN);
    }
  }, [currentProvider, isUsingThePartnerTool]);

  const goBackToSafety = () => {
    navigate(ORGANISATION_DETAIL, { domain: organisation?.domain || '' });
  };

  return {
    isLoggedIn,
    isUserNotVerified,
    logOutURL,
    hasUserLoaded,
    userError,
    goBackToSafety,
    shouldGoToTheDashboard,
    isNoOrganisationSelected,
    organisationLoadError:
      organisationError || !hasAccess(Route.ORGANISATION_ROOT) || localOrganisationError,
    hasRolesLoaded,
  };
};
